import { pathNode, pathExtrema, type PathAnchor } from "./vector-path";
import { unsupported } from "./legacy-import-text";

/** Fabric serializes pen/Bezier paths as absolute M/L/Q/C/Z commands. */
export function importLegacyPath(id: string, path: unknown) {
  if (!Array.isArray(path) || path.length > 20000) return unsupported("контур");
  const anchors: PathAnchor[] = [],
    bounds: PathAnchor[] = [];
  let closed = false;
  for (const command of path) {
    if (!Array.isArray(command)) return unsupported("команду контура");
    const [rawType, ...v] = command;
    const type = rawType === "z" ? "Z" : rawType;
    const size = { M: 2, L: 2, C: 6, Q: 4, Z: 0 }[type as string];
    if (
      size === undefined ||
      v.length !== size ||
      v.some(
        (n) =>
          typeof n !== "number" || !Number.isFinite(n) || Math.abs(n) > 1e6,
      ) ||
      closed
    )
      return unsupported("команду или составной контур");
    const previous = anchors.at(-1);
    if (type === "M") {
      if (previous) return unsupported("составной контур");
      anchors.push({ x: v[0], y: v[1] });
    } else {
      if (!previous) return unsupported("начало контура");
      if (type === "L") anchors.push({ x: v[0], y: v[1] });
      if (type === "C") {
        bounds.push(
          ...pathExtrema(
            [
              { ...previous, outgoing: { x: v[0], y: v[1] } },
              { x: v[4], y: v[5], incoming: { x: v[2], y: v[3] } },
            ],
            false,
          ),
        );
        previous.outgoing = { x: v[0], y: v[1] };
        anchors.push({ x: v[4], y: v[5], incoming: { x: v[2], y: v[3] } });
      }
      if (type === "Q") {
        // Old .3dcd bounds use the quadratic control twice as a cubic control.
        // Preserve that frame/offset at the import boundary, but keep exact Q geometry.
        bounds.push(
          ...pathExtrema(
            [
              { ...previous, outgoing: { x: v[0], y: v[1] } },
              { x: v[2], y: v[3], incoming: { x: v[0], y: v[1] } },
            ],
            false,
          ),
        );
        previous.outgoing = {
          x: previous.x + (2 * (v[0] - previous.x)) / 3,
          y: previous.y + (2 * (v[1] - previous.y)) / 3,
        };
        anchors.push({
          x: v[2],
          y: v[3],
          incoming: {
            x: v[2] + (2 * (v[0] - v[2])) / 3,
            y: v[3] + (2 * (v[1] - v[3])) / 3,
          },
        });
      }
      if (type === "Z") closed = true;
    }
  }
  if (closed && anchors.length > 2) {
    const a = anchors[0]!,
      z = anchors.at(-1)!;
    if (a.x === z.x && a.y === z.y) {
      if (z.incoming) a.incoming = z.incoming;
      anchors.pop();
    }
  }
  if (anchors.length < 2) return unsupported("пустой контур");
  const node = pathNode(id, anchors, closed),
    points = [...anchors, ...bounds];
  const left = Math.min(...points.map((p) => p.x)),
    top = Math.min(...points.map((p) => p.y));
  const width = Math.max(1, Math.max(...points.map((p) => p.x)) - left),
    height = Math.max(1, Math.max(...points.map((p) => p.y)) - top);
  const norm = (p: { x: number; y: number }) => ({
    x: (p.x - left) / width - 0.5,
    y: (p.y - top) / height - 0.5,
  });
  node.geometry = {
    width,
    height,
    vector: {
      anchors: anchors.map((a) => ({
        ...norm(a),
        ...(a.incoming ? { incoming: norm(a.incoming) } : {}),
        ...(a.outgoing ? { outgoing: norm(a.outgoing) } : {}),
      })),
      closed,
      arrow: false,
    },
  };
  node.transform = {
    ...node.transform,
    x: left + width / 2,
    y: top + height / 2,
  };
  return node;
}
